/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';

interface MermaidDiagramProps {
  diagram: string;
  className?: string;
}

// Initialize mermaid with a clean configuration
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'ui-sans-serif, system-ui, sans-serif',
});

/** Strip optional markdown code fence so mermaid gets raw diagram text. */
function normalizeDiagramText(text: string): string {
  const trimmed = text.trim();
  const fence = '```';
  if (trimmed.startsWith(fence)) {
    const afterOpen = trimmed.slice(fence.length).replace(/^mermaid\s*\n?/i, '').trimStart();
    const endIdx = afterOpen.indexOf(fence);
    if (endIdx !== -1) {
      return afterOpen.slice(0, endIdx).trimEnd();
    }
    return afterOpen;
  }
  return trimmed;
}

/**
 * Quote node labels that contain '/' so Mermaid's lexer doesn't treat them as syntax.
 * Converts stadium/shape labels like [/api/products] to ["/api/products"] (rectangle with same text).
 */
function quoteLabelsWithSlashes(text: string): string {
  return text.replace(/\[\/([^\]]+)\]/g, (_, label: string) => {
    if (label.includes('/') && !label.includes('"')) {
      return `["/${label}"]`;
    }
    return `[/${label}]`;
  });
}

export function MermaidDiagram({ diagram, className = '' }: MermaidDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [svgContent, setSvgContent] = useState<string>('');

  useEffect(() => {
    if (!diagram) return;

    const normalized = normalizeDiagramText(diagram);
    if (!normalized) return;

    const diagramForRender = quoteLabelsWithSlashes(normalized);
    let cancelled = false;

    const renderDiagram = async () => {
      try {
        setError(null);
        setSvgContent('');

        const id = `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        // Do not pass container: mermaid mutates it (innerHTML, removeChild) and conflicts with React.
        const { svg } = await mermaid.render(id, diagramForRender);

        if (cancelled) return;
        setSvgContent(svg);
      } catch (err: any) {
        if (cancelled) return;
        console.error('Mermaid rendering error:', err);
        setError(err.message || 'Failed to render diagram');
        setSvgContent('');
      }
    };

    void renderDiagram();
    return () => {
      cancelled = true;
    };
  }, [diagram]);

  if (error) {
    return (
      <div className={`p-4 bg-destructive/10 border border-destructive/30 rounded-lg ${className}`}>
        <p className="text-sm text-destructive font-medium mb-2">Failed to render diagram</p>
        <pre className="text-xs text-destructive/80 whitespace-pre-wrap">{error}</pre>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`overflow-auto ${className}`}>
      {!svgContent ? (
        <div className="flex items-center justify-center p-8 bg-muted/30 rounded-lg min-h-[200px]">
          <div className="animate-pulse text-muted-foreground">Loading diagram...</div>
        </div>
      ) : (
        <div dangerouslySetInnerHTML={{ __html: svgContent }} />
      )}
    </div>
  );
}

